package com.partejar.app.feature.profile.presentation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.tooling.preview.Preview
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarSkeleton
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.ThemeMode
import com.partejar.app.feature.profile.data.UserProfile
import com.partejar.app.feature.profile.data.UserProfessionalRegistration
import com.partejar.app.feature.profile.data.UserProfessionalRegistrationInput
import org.koin.compose.koinInject

@Composable
fun ProfileScreen(
    onBack: () -> Unit,
    viewModel: ProfileViewModel = koinInject()
) {
    val state by viewModel.uiState.collectAsState()
    val submitState by viewModel.submitState.collectAsState()
    val registrationsState by viewModel.registrationsState.collectAsState()
    ProfileLayout(
        state = state,
        submitState = submitState,
        registrationsState = registrationsState,
        onBack = onBack,
        onRetry = viewModel::load,
        onSaveName = viewModel::saveName,
        onDismissError = viewModel::clearSubmitState,
        onRetryRegistrations = viewModel::loadRegistrations,
        onSaveRegistration = viewModel::saveRegistration,
        onDeactivateRegistration = viewModel::deactivateRegistration
    )
}

@Composable
private fun ProfileLayout(
    state: ProfileUiState,
    submitState: ProfileSubmitState,
    registrationsState: ProfessionalRegistrationsUiState,
    onBack: () -> Unit,
    onRetry: () -> Unit,
    onSaveName: (String, String?) -> Unit,
    onDismissError: () -> Unit,
    onRetryRegistrations: () -> Unit,
    onSaveRegistration: (Long?, UserProfessionalRegistrationInput) -> Unit,
    onDeactivateRegistration: (Long) -> Unit
) {
    val spacing = PartejarTheme.spacing
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            PartejarTopBar(
                title = "Perfil e conta",
                leadingContent = {
                    IconButton(onClick = onBack) {
                        Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
                    }
                }
            )
        }
    ) { padding ->
        when (state) {
            ProfileUiState.Loading -> ProfileLoading(Modifier.padding(padding))
            is ProfileUiState.Error -> ProfileLoadError(
                message = state.message,
                onRetry = onRetry,
                modifier = Modifier.padding(padding).padding(spacing.space24)
            )

            is ProfileUiState.Content -> ProfileContent(
                profile = state.profile,
                submitState = submitState,
                registrationsState = registrationsState,
                onSaveName = onSaveName,
                onDismissError = onDismissError,
                onRetryRegistrations = onRetryRegistrations,
                onSaveRegistration = onSaveRegistration,
                onDeactivateRegistration = onDeactivateRegistration,
                modifier = Modifier.padding(padding)
            )
        }
    }
}

@Composable
private fun ProfileContent(
    profile: UserProfile,
    submitState: ProfileSubmitState,
    registrationsState: ProfessionalRegistrationsUiState,
    onSaveName: (String, String?) -> Unit,
    onDismissError: () -> Unit,
    onRetryRegistrations: () -> Unit,
    onSaveRegistration: (Long?, UserProfessionalRegistrationInput) -> Unit,
    onDeactivateRegistration: (Long) -> Unit,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    var editing by remember(profile.id) { mutableStateOf(false) }
    var name by remember(profile.id) { mutableStateOf(profile.name) }
    var cpf by remember(profile.id) { mutableStateOf(profile.cpf.orEmpty()) }
    LaunchedEffect(profile.name) {
        if (!editing) name = profile.name
    }
    LaunchedEffect(submitState) {
        if (submitState is ProfileSubmitState.Success) editing = false
    }
    LazyColumn(
        modifier = modifier.fillMaxSize(),
        contentPadding = PaddingValues(
            start = spacing.space16,
            top = spacing.space16,
            end = spacing.space16,
            bottom = spacing.space32
        ),
        verticalArrangement = Arrangement.spacedBy(spacing.space16)
    ) {
        item { ProfileIdentityCard(profile) }
        item {
            PartejarCard(modifier = Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
                    Text("Dados pessoais", style = MaterialTheme.typography.titleMedium)
                    PartejarTextField(
                        value = name,
                        onValueChange = { name = it },
                        label = "Nome",
                        enabled = editing && submitState !is ProfileSubmitState.Submitting,
                        isError = submitState is ProfileSubmitState.Error,
                        supportingText = (submitState as? ProfileSubmitState.Error)?.message
                    )
                    PartejarTextField(
                        value = cpf,
                        onValueChange = { cpf = it },
                        label = "CPF",
                        enabled = editing && submitState !is ProfileSubmitState.Submitting
                    )
                    PartejarTextField(
                        value = profile.email,
                        onValueChange = {},
                        label = "E-mail",
                        enabled = false,
                        supportingText = "O e-mail não pode ser alterado nesta etapa."
                    )
                    if (editing) {
                        PartejarButton(
                            text = "Salvar alterações",
                            onClick = { onSaveName(name, cpf.ifBlank { null }) },
                            isLoading = submitState is ProfileSubmitState.Submitting
                        )
                        PartejarButton(
                            text = "Cancelar",
                            onClick = {
                                name = profile.name
                                editing = false
                                onDismissError()
                            },
                            enabled = submitState !is ProfileSubmitState.Submitting,
                            variant = PartejarButtonVariant.SECONDARY
                        )
                    } else {
                        PartejarButton(
                            text = "Editar nome",
                            onClick = { editing = true },
                            variant = PartejarButtonVariant.SECONDARY
                        )
                    }
                }
            }
        }
        item {
            ProfessionalRegistrationsCard(
                state = registrationsState,
                submitState = submitState,
                onRetry = onRetryRegistrations,
                onSave = onSaveRegistration,
                onDeactivate = onDeactivateRegistration,
                onDismissError = onDismissError
            )
        }
        item {
            PartejarAlert(title = "Foto de perfil", tone = PartejarAlertTone.INFO) {
                Text("A foto estará disponível quando houver armazenamento seguro para os arquivos.")
            }
        }
    }
}

@Composable
private fun ProfessionalRegistrationsCard(
    state: ProfessionalRegistrationsUiState,
    submitState: ProfileSubmitState,
    onRetry: () -> Unit,
    onSave: (Long?, UserProfessionalRegistrationInput) -> Unit,
    onDeactivate: (Long) -> Unit,
    onDismissError: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    var showingEditor by remember { mutableStateOf(false) }
    var editing by remember { mutableStateOf<UserProfessionalRegistration?>(null) }

    PartejarCard(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(spacing.space16),
            verticalArrangement = Arrangement.spacedBy(spacing.space12)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16) // Dá uma margem de segurança entre o texto e o botão
            ) {
                Text(
                    text = "Registros profissionais",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.titleMedium
                )

                if (!showingEditor) {
                    PartejarButton(
                        text = "Adicionar",
                        onClick = { editing = null; showingEditor = true },
                        variant = PartejarButtonVariant.SECONDARY,
                        modifier = Modifier.width(IntrinsicSize.Max)
                    )
                }
            }

            when (state) {
                ProfessionalRegistrationsUiState.Loading -> Column(
                    verticalArrangement = Arrangement.spacedBy(spacing.space8)
                ) {
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.56f).height(spacing.space16))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.76f).height(spacing.space12))
                }

                is ProfessionalRegistrationsUiState.Error -> {
                    PartejarAlert(
                        title = "Registros indisponíveis",
                        tone = PartejarAlertTone.ERROR
                    ) { Text(state.message) }
                    PartejarButton(
                        text = "Tentar novamente",
                        onClick = onRetry,
                        variant = PartejarButtonVariant.SECONDARY
                    )
                }

                is ProfessionalRegistrationsUiState.Content -> if (state.registrations.isEmpty()) {
                    Text(
                        text = "Nenhum registro profissional cadastrado.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                } else state.registrations.forEach { registration ->
                    ProfessionalRegistrationRow(
                        registration = registration,
                        onEdit = { editing = registration; showingEditor = true },
                        onDeactivate = { onDeactivate(registration.id) },
                        busy = submitState is ProfileSubmitState.Submitting
                    )
                }
            }

            if (showingEditor) {
                ProfessionalRegistrationEditor(
                    registration = editing,
                    submitting = submitState is ProfileSubmitState.Submitting,
                    onSave = { input -> onSave(editing?.id, input) },
                    onCancel = { showingEditor = false; editing = null; onDismissError() }
                )
            }

            if (submitState is ProfileSubmitState.Error) {
                PartejarAlert(
                    title = "Não foi possível concluir",
                    tone = PartejarAlertTone.ERROR
                ) { Text(submitState.message) }
            }

            LaunchedEffect(submitState) {
                if (submitState is ProfileSubmitState.Success) {
                    showingEditor = false
                    editing = null
                    onDismissError()
                }
            }
        }
    }
}

@Composable
private fun ProfessionalRegistrationRow(
    registration: UserProfessionalRegistration,
    onEdit: () -> Unit,
    onDeactivate: () -> Unit,
    busy: Boolean
) {
    val spacing = PartejarTheme.spacing
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
        Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Text(
                registration.category.replace('_', ' ').lowercase()
                    .replaceFirstChar { it.titlecase() },
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.bodyLarge
            )
            Text(
                if (registration.active) "Ativo" else "Inativo",
                style = MaterialTheme.typography.labelMedium,
                color = if (registration.active) PartejarTheme.colors.success else MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        Text(
            registration.label,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        if (registration.active) {
            Row(horizontalArrangement = Arrangement.spacedBy(spacing.space8)) {
                PartejarButton(
                    text = "Editar",
                    onClick = onEdit,
                    enabled = !busy,
                    variant = PartejarButtonVariant.SECONDARY
                )
                PartejarButton(
                    text = "Inativar",
                    onClick = onDeactivate,
                    enabled = !busy,
                    variant = PartejarButtonVariant.SECONDARY
                )
            }
        }
    }
}

@Composable
private fun ProfessionalRegistrationEditor(
    registration: UserProfessionalRegistration?,
    submitting: Boolean,
    onSave: (UserProfessionalRegistrationInput) -> Unit,
    onCancel: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    var category by remember(registration?.id) { mutableStateOf(registration?.category.orEmpty()) }
    var council by remember(registration?.id) { mutableStateOf(registration?.council.orEmpty()) }
    var number by remember(registration?.id) { mutableStateOf(registration?.number.orEmpty()) }
    var state by remember(registration?.id) { mutableStateOf(registration?.state.orEmpty()) }
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
        Text(
            if (registration == null) "Novo registro" else "Editar registro",
            style = MaterialTheme.typography.titleSmall
        )
        PartejarTextField(
            value = category,
            onValueChange = { category = it },
            label = "Categoria",
            supportingText = "ENFERMEIRA_OBSTETRICA, DOULA, MEDICO ou OUTRO",
            enabled = !submitting
        )
        PartejarTextField(
            value = council,
            onValueChange = { council = it },
            label = "Conselho",
            enabled = !submitting
        )
        PartejarTextField(
            value = number,
            onValueChange = { number = it },
            label = "Número",
            enabled = !submitting
        )
        PartejarTextField(
            value = state,
            onValueChange = { state = it.uppercase() },
            label = "UF",
            enabled = !submitting
        )
        PartejarButton(
            text = if (registration == null) "Cadastrar registro" else "Salvar registro",
            onClick = {
                onSave(
                    UserProfessionalRegistrationInput(
                        category.trim().uppercase(),
                        council.trim().uppercase(),
                        number.trim(),
                        state.trim().uppercase()
                    )
                )
            },
            isLoading = submitting
        )
        PartejarButton(
            text = "Cancelar",
            onClick = onCancel,
            enabled = !submitting,
            variant = PartejarButtonVariant.SECONDARY
        )
    }
}

@Composable
private fun ProfileIdentityCard(profile: UserProfile) {
    val spacing = PartejarTheme.spacing
    PartejarCard(modifier = Modifier.fillMaxWidth()) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Surface(
                modifier = Modifier
                    .size(PartejarTheme.dimensions.controlHeight)
                    .clearAndSetSemantics { },
                shape = PartejarShapes.pill,
                color = MaterialTheme.colorScheme.primaryContainer,
                contentColor = MaterialTheme.colorScheme.onPrimaryContainer
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(profile.initials(), style = MaterialTheme.typography.titleMedium)
                }
            }
            Column(
                modifier = Modifier.padding(start = spacing.space12),
                verticalArrangement = Arrangement.spacedBy(spacing.space4)
            ) {
                Text(profile.name, style = MaterialTheme.typography.titleLarge)
                Text(
                    profile.email,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
private fun ProfileLoading(modifier: Modifier = Modifier) = LazyColumn(
    modifier = modifier.fillMaxSize(),
    contentPadding = PaddingValues(PartejarTheme.spacing.space16),
    verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)
) {
    item {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)
        ) {
            PartejarSkeleton(
                Modifier.size(PartejarTheme.spacing.space64),
                shape = PartejarShapes.pill
            )
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.58f).height(PartejarTheme.spacing.space16))
                PartejarSkeletonText(Modifier.fillMaxWidth(0.82f).height(PartejarTheme.spacing.space12))
            }
        }
    }
    items(2) {
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.46f).height(PartejarTheme.spacing.space16))
                PartejarSkeletonText(Modifier.fillMaxWidth().height(PartejarTheme.spacing.space12))
                PartejarSkeletonText(Modifier.fillMaxWidth(0.72f).height(PartejarTheme.spacing.space12))
            }
        }
    }
}

@Composable
private fun ProfileLoadError(message: String, onRetry: () -> Unit, modifier: Modifier = Modifier) =
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)
    ) {
        PartejarAlert(
            title = "Perfil indisponível",
            tone = PartejarAlertTone.ERROR
        ) { Text(message) }
        PartejarButton(text = "Tentar novamente", onClick = onRetry)
    }

internal fun UserProfile.initials(): String = name
    .trim()
    .split(Regex("\\s+"))
    .filter(String::isNotBlank)
    .take(2)
    .joinToString(separator = "") { it.first().uppercase() }
    .ifBlank { "?" }

private val previewProfile = UserProfile(1, "Ana Souza", null, "ana@partejar.local")

@Preview(name = "Perfil · Light", widthDp = 390, heightDp = 844)
@Composable
private fun ProfileLightPreview() = PartejarTheme(themeMode = ThemeMode.LIGHT) {
    ProfileLayout(
        ProfileUiState.Content(previewProfile),
        ProfileSubmitState.Idle,
        ProfessionalRegistrationsUiState.Content(emptyList()),
        {},
        {},
        { _, _ -> },
        {},
        {},
        { _, _ -> },
        {})
}

@Preview(name = "Perfil · Dark", widthDp = 390, heightDp = 844)
@Composable
private fun ProfileDarkPreview() = PartejarTheme(themeMode = ThemeMode.DARK) {
    ProfileLayout(
        ProfileUiState.Content(previewProfile),
        ProfileSubmitState.Idle,
        ProfessionalRegistrationsUiState.Content(emptyList()),
        {},
        {},
        { _, _ -> },
        {},
        {},
        { _, _ -> },
        {})
}

@Preview(name = "Perfil · Erro", widthDp = 390, heightDp = 844)
@Composable
private fun ProfileErrorPreview() = PartejarTheme {
    ProfileLayout(
        ProfileUiState.Error("Não foi possível carregar o perfil."),
        ProfileSubmitState.Idle,
        ProfessionalRegistrationsUiState.Content(emptyList()),
        {},
        {},
        { _, _ -> },
        {},
        {},
        { _, _ -> },
        {})
}
